import { useEffect, useState } from 'react';

// Public, read-only mirror of the GitHub contributions calendar. GitHub's own
// contribution data only comes from the authenticated GraphQL API, and this is
// a static frontend with nowhere to hide a token, so the calendar is read from
// this proxy instead.
const API_BASE =
  process.env.REACT_APP_GITHUB_CONTRIBUTIONS_API ||
  'https://github-contributions-api.jogruber.de/v4';

// Fetches the last twelve months of contributions for `username`.
// Returns { days, total, status } where status is 'loading' | 'ready' | 'error'
// and days is a flat, date-ascending list of { date, count, level }.
export const useGithubContributions = (username) => {
  const [state, setState] = useState({ days: [], total: 0, status: 'loading' });

  useEffect(() => {
    if (!username) {
      setState({ days: [], total: 0, status: 'error' });
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/${encodeURIComponent(username)}?y=last`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`GitHub contributions request failed: ${res.status}`);

        const data = await res.json();
        const days = Array.isArray(data.contributions) ? data.contributions : [];
        if (!days.length) throw new Error('GitHub contributions response was empty');

        // `total` is keyed by year plus a "lastYear" rollup; fall back to
        // summing the days if the shape ever changes.
        const total =
          typeof data.total?.lastYear === 'number'
            ? data.total.lastYear
            : days.reduce((sum, day) => sum + (day.count || 0), 0);

        setState({ days, total, status: 'ready' });
      } catch (err) {
        if (err.name === 'AbortError') return;
        setState({ days: [], total: 0, status: 'error' });
      }
    };

    load();
    return () => controller.abort();
  }, [username]);

  return state;
};

export default useGithubContributions;
